// Account.jsx — root component, wires all sub-components together
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CustomerAuthService } from "../../services/CustomerAuthServices";
import { isCustomerLoggedIn } from "../../utils/CustomerAuth";
import { CartServices } from "../../services/CartServices";
import { WishlistServices } from "../../services/WishlistServices";
import { OrderServices } from "../../services/OrderServices";

import { CSS } from "./Accountcss";
import AccountSidebar from "./AccountSidebar";
import AccountOverview from "./AccountOverview";
import AccountSettings from "./AccountSettings";
import AccountAuth from "./AccountAuth";
import MyOrders from "./MyOrders";
import MyCart from "./MyCart";
import MyWishlist from "./MyWishlist";
import DeliveryAddress from "./DeliveryAddress";

function useInjectCSS() {
  useEffect(() => {
    if (document.getElementById("ap-css")) return;
    const el = document.createElement("style");
    el.id = "ap-css";
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);
}

const Account = () => {
  useInjectCSS();
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn]   = useState(isCustomerLoggedIn());
  const [dashView, setDashView]       = useState("overview");
  const [userData, setUserData]       = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [cartCount, setCartCount]     = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [ordersCount, setOrdersCount] = useState(0);
  const [cartSubtotal, setCartSubtotal] = useState(0);
  const [msg, setMsg]                 = useState({ type: "", text: "" });

  useEffect(() => {
    const check = () => setIsLoggedIn(isCustomerLoggedIn());
    check();
    const evts = ["customer_logged_in", "customer_logged_out", "customer_auth_changed"];
    evts.forEach((e) => window.addEventListener(e, check));
    return () => evts.forEach((e) => window.removeEventListener(e, check));
  }, []);

  useEffect(() => {
    if (location.pathname === "/account/settings") setDashView("settings");
    else if (location.pathname === "/account/orders")   setDashView("orders");
    else if (location.pathname === "/account/cart")     setDashView("cart");
    else if (location.pathname === "/account/wishlist") setDashView("wishlist");
    else if (location.pathname === "/account/delivery-address") setDashView("delivery-address");
    else if (location.pathname === "/account")          setDashView("overview");
  }, [location.pathname]);

  const computeSubtotal = (items) =>
    (items || []).reduce((s, it) => {
      const q = Number(it?.quantity ?? 1);
      const p =
        Number(it?.product_info?.current_price_override ?? it?.productInfo?.current_price_override ?? "") ||
        Number(it?.product?.base_current_price ?? 0) ||
        Number(it?.unit_price ?? 0) || 0;
      return s + p * q;
    }, 0);

  const loadDashboardData = async () => {
    setStatsLoading(true);
    try {
      const [cart, wl, orders] = await Promise.allSettled([
        CartServices.getCart(),
        WishlistServices.getWishlist(),
        OrderServices.getMyOrders(),
      ]);
      const ci = cart.status === "fulfilled" && Array.isArray(cart.value) ? cart.value : [];
      const wi = wl.status === "fulfilled" && Array.isArray(wl.value) ? wl.value : [];
      const oi = orders.status === "fulfilled"
        ? (Array.isArray(orders.value?.data) ? orders.value.data : Array.isArray(orders.value) ? orders.value : [])
        : [];
      setCartCount(ci.length);
      setWishlistCount(wi.length);
      setOrdersCount(oi.length);
      setCartSubtotal(computeSubtotal(ci));
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    const run = async () => {
      if (!userData) {
        setLoadingUser(true);
        try { setUserData(await CustomerAuthService.me()); }
        catch (e) { console.error(e); }
        finally { setLoadingUser(false); }
      }
      loadDashboardData();
    };
    run();
  }, [isLoggedIn]); // eslint-disable-line

  useEffect(() => {
    if (!isLoggedIn) return;
    const refresh = () => loadDashboardData();
    window.addEventListener("cart_updated", refresh);
    window.addEventListener("wishlist_updated", refresh);
    window.addEventListener("orders_updated", refresh);
    return () => {
      window.removeEventListener("cart_updated", refresh);
      window.removeEventListener("wishlist_updated", refresh);
      window.removeEventListener("orders_updated", refresh);
    };
  }, [isLoggedIn]); // eslint-disable-line

  const memberSince = useMemo(() => {
    const d = userData?.created_at ? new Date(userData.created_at) : null;
    return d ? d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";
  }, [userData]);

  const initials = userData?.name
    ? userData.name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  const handleLogout = async () => {
    try {
      await CustomerAuthService.logout();
      setUserData(null); setIsLoggedIn(false);
      setCartCount(0); setWishlistCount(0); setOrdersCount(0); setCartSubtotal(0);
      navigate("/login");
    } catch {
      setMsg({ type: "err", text: "Logout failed." });
    }
  };

  if (isLoggedIn) {
    return (
      <div className="ap-root">
        <div className="ap-dash">
          <AccountSidebar
            dashView={dashView}
            setDashView={setDashView}
            userData={userData}
            initials={initials}
            ordersCount={ordersCount}
            wishlistCount={wishlistCount}
            cartCount={cartCount}
            onLogout={handleLogout}
          />
          <main className="ap-main">
            {dashView === "overview" && (
              <AccountOverview
                userData={userData}
                loadingUser={loadingUser}
                statsLoading={statsLoading}
                cartCount={cartCount}
                wishlistCount={wishlistCount}
                ordersCount={ordersCount}
                cartSubtotal={cartSubtotal}
                memberSince={memberSince}
                msg={msg}
                onRefresh={loadDashboardData}
                onGoToSettings={() => setDashView("settings")}
                onGoToOrders={() => setDashView("orders")}
                onGoToWishlist={() => setDashView("wishlist")}
                onGoToCart={() => setDashView("cart")}
                onGoToDeliveryAddress={() => setDashView("delivery-address")}
              />
            )}
            {dashView === "settings" && (
              <AccountSettings
                userData={userData}
                setUserData={setUserData}
                onBack={() => setDashView("overview")}
              />
            )}
            {dashView === "orders" && (
              <MyOrders onBack={() => setDashView("overview")} />
            )}
            {dashView === "cart" && (
              <MyCart onBack={() => setDashView("overview")} />
            )}
            {dashView === "wishlist" && (
              <MyWishlist onBack={() => setDashView("overview")} />
            )}
            {dashView === "delivery-address" && (
              <DeliveryAddress onBack={() => setDashView("overview")} />
            )}
          </main>
        </div>
      </div>
    );
  }

  const initialMode = ["/signup", "/register"].includes(location.pathname)
    ? "register"
    : "login";

  return (
    <div className="ap-root">
      <AccountAuth
        initialMode={initialMode}
        onLoginSuccess={() => setIsLoggedIn(true)}
      />
    </div>
  );
};

export default Account;